import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import type { Zone, Location, DeviceStatus, CreateSerializedItemInput, SkuSearchResult } from '../../types';
import { useCatalogSkuSearch } from '../../hooks/useWmsSkus';
import { useDebounce } from '../../hooks/useDebounce';
import { validateSerialNumber } from '../../utils/validation';

interface SerializedItemFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateSerializedItemInput) => Promise<void>;
  zones: Zone[];
  locations: Location[];
  isLoading?: boolean;
}

const deviceStatusOptions: { value: DeviceStatus; label: string }[] = [
  { value: 'valid', label: 'Valid' },
  { value: 'verify', label: 'Verify' },
  { value: 'test', label: 'Test' },
  { value: 'dev', label: 'Dev' },
  { value: 'dead', label: 'Dead' },
];

export function SerializedItemFormModal({
  open,
  onClose,
  onSubmit,
  zones,
  locations,
  isLoading,
}: SerializedItemFormModalProps) {
  const [serialNumber, setSerialNumber] = useState('');
  const [macAddress, setMacAddress] = useState('');
  const [devId, setDevId] = useState('');
  const [skuSearch, setSkuSearch] = useState('');
  const [selectedSku, setSelectedSku] = useState<SkuSearchResult | null>(null);
  const [selectedZoneId, setSelectedZoneId] = useState('');
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [deviceStatus, setDeviceStatus] = useState<DeviceStatus>('valid');
  const [calibrationDueDate, setCalibrationDueDate] = useState('');
  const [firmwareVersion, setFirmwareVersion] = useState('');
  const [hardwareVersion, setHardwareVersion] = useState('');
  const [notes, setNotes] = useState('');

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setSerialNumber('');
      setMacAddress('');
      setDevId('');
      setSkuSearch('');
      setSelectedSku(null);
      setSelectedZoneId('');
      setSelectedLocationId('');
      setDeviceStatus('valid');
      setCalibrationDueDate('');
      setFirmwareVersion('');
      setHardwareVersion('');
      setNotes('');
    }
  }, [open]);

  // Debounce search to reduce API calls (Issue #14)
  const debouncedSearch = useDebounce(skuSearch, 300);
  const { data: skuResults } = useCatalogSkuSearch(debouncedSearch);

  const filteredLocations = selectedZoneId
    ? locations.filter((l) => l.zone_id === selectedZoneId)
    : locations;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!serialNumber || !selectedSku || !selectedLocationId) return;

    // Issue #22: Validate serial number format
    const serialValidation = validateSerialNumber(serialNumber);
    if (!serialValidation.valid) {
      toast.error(serialValidation.error || 'Invalid serial number');
      return;
    }

    // Issue #20: Add try-catch for unhandled promise rejections
    // Issue #18: Enhance error messages with details
    try {
      await onSubmit({
        serial_number: serialNumber,
        mongo_sku_id: selectedSku.id,
        sku_code: selectedSku.sku,
        location_id: selectedLocationId,
        mac_address: macAddress || undefined,
        dev_id: devId || undefined,
        device_status: deviceStatus,
        calibration_due_date: calibrationDueDate || undefined,
        firmware_version: firmwareVersion || undefined,
        hardware_version: hardwareVersion || undefined,
        notes: notes || undefined,
      });

      // Only reset and close on success
      setSerialNumber('');
      setMacAddress('');
      setDevId('');
      setSkuSearch('');
      setSelectedSku(null);
      setSelectedZoneId('');
      setSelectedLocationId('');
      setDeviceStatus('valid');
      setCalibrationDueDate('');
      setFirmwareVersion('');
      setHardwareVersion('');
      setNotes('');
      toast.success('Serialized item created successfully');
      onClose();
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Unknown error occurred';
      toast.error(`Failed to create serialized item: ${message}`);
      console.error('Error creating serialized item:', error);
      // Keep form open so user can retry
    }
  };

  const handleSelectSku = (sku: SkuSearchResult) => {
    setSelectedSku(sku);
    setSkuSearch(sku.sku);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Serialized Item</DialogTitle>
          <DialogDescription>
            Register a new device with serial number tracking.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Serial Number */}
          <div>
            <label className="text-sm font-medium">Serial Number *</label>
            <Input
              value={serialNumber}
              onChange={(e) => setSerialNumber(e.target.value)}
              placeholder="Enter serial number"
              required
              className="mt-1"
            />
          </div>

          {/* MAC Address & Device ID */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">MAC Address</label>
              <Input
                value={macAddress}
                onChange={(e) => setMacAddress(e.target.value)}
                placeholder="00:00:00:00:00:00"
                className="mt-1 font-mono"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Device ID</label>
              <Input
                value={devId}
                onChange={(e) => setDevId(e.target.value)}
                placeholder="Device ID"
                className="mt-1 font-mono"
              />
            </div>
          </div>

          {/* SKU Search */}
          <div>
            <label className="text-sm font-medium">SKU *</label>
            <div className="relative mt-1">
              <Input
                value={skuSearch}
                onChange={(e) => {
                  setSkuSearch(e.target.value);
                  if (selectedSku && e.target.value !== selectedSku.sku) {
                    setSelectedSku(null);
                  }
                }}
                placeholder="Search for SKU..."
              />
              {skuResults && skuResults.length > 0 && !selectedSku && (
                <div className="absolute z-10 w-full mt-1 bg-popover border rounded-md shadow-lg max-h-48 overflow-y-auto">
                  {skuResults.map((sku) => (
                    <button
                      key={sku.id}
                      type="button"
                      className="w-full px-3 py-2 text-left hover:bg-accent text-sm"
                      onClick={() => handleSelectSku(sku)}
                    >
                      <div className="font-mono font-medium">{sku.sku}</div>
                      <div className="text-muted-foreground text-xs truncate">
                        {sku.nameEn || sku.nameFr}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {selectedSku && (
              <p className="mt-1 text-sm text-muted-foreground">
                {selectedSku.manufacturerName ? `${selectedSku.manufacturerName} · ` : ''}
                {selectedSku.nameEn || selectedSku.nameFr}
              </p>
            )}
          </div>

          {/* Zone & Location */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Zone *</label>
              <Select value={selectedZoneId} onValueChange={(v) => {
                setSelectedZoneId(v);
                setSelectedLocationId('');
              }}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select zone" />
                </SelectTrigger>
                <SelectContent>
                  {zones.map((zone) => (
                    <SelectItem key={zone.id} value={zone.id}>
                      {zone.code} - {zone.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Location *</label>
              <Select
                value={selectedLocationId}
                onValueChange={setSelectedLocationId}
                disabled={!selectedZoneId}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select bin" />
                </SelectTrigger>
                <SelectContent>
                  {filteredLocations.map((location) => (
                    <SelectItem key={location.id} value={location.id}>
                      {location.bin_code}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Device Status (5S) */}
          <div>
            <label className="text-sm font-medium">5S Status</label>
            <Select value={deviceStatus} onValueChange={(v) => setDeviceStatus(v as DeviceStatus)}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {deviceStatusOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Calibration & Versions */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium">Calibration Due</label>
              <Input
                type="date"
                value={calibrationDueDate}
                onChange={(e) => setCalibrationDueDate(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Firmware</label>
              <Input
                value={firmwareVersion}
                onChange={(e) => setFirmwareVersion(e.target.value)}
                placeholder="v1.0.0"
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Hardware</label>
              <Input
                value={hardwareVersion}
                onChange={(e) => setHardwareVersion(e.target.value)}
                placeholder="Rev A"
                className="mt-1"
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="text-sm font-medium">Notes</label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes..."
              className="mt-1"
              rows={2}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading || !serialNumber || !selectedSku || !selectedLocationId}
            >
              {isLoading ? 'Adding...' : 'Add Item'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
